// Fix-once speaker panel. Owner: WP-6 (BUILD 1).
//
// One chip per voice, the most talk time first ("38 min · 143 rows"), with
// sample playback through the canvas player. Naming a voice renames every
// row of it; a voice can be a guest, a company member, not a participant, or
// merged into another voice (and separated again). Each write reloads the
// meeting and marks the notes stale; the panel says when the notes have
// caught up. Changes made from the transcript's row popover announce
// themselves here, because only this slot receives the canvas's `onChanged`.
// BUILD 2's voice pass renders through the meeting-voice.tsx slots (WP2-4):
// suggestions on each chip, the status line and the review-queue entry.
// Copy: ui/i18n/ns/meetings-speakers.ts. Props: meeting-slots.ts.
import './meeting-speakers.css';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { formatCopy } from '@/i18n/ns';
import { useWorkspace } from './context';
import { meetingsApi } from './meetings-api';
import {
  mergedInto,
  panelVoices,
  sampleRanges,
  talkMinutes,
  voiceOfPerson,
} from './meeting-speaker-model';
import {
  failureCopy,
  participantFor,
  SpeakerTargetPicker,
  targetName,
  useCompanyMembers,
  useSpeakerChanges,
  useSpeakersCopy,
  voiceName,
  type SpeakersCopy,
  type SpeakerTarget,
} from './meeting-speaker-popover';
import type { MeetingPlayerRef, MeetingSpeakersPanelProps } from './meeting-slots';
import { SpeakerVoiceSuggestions, VoiceReviewEntry, VoiceStatusLine } from './meeting-voice';
import { MeetingVoiceEnrolment } from './meeting-voice-enrolment';
import type { CompanyMeeting, MeetingSpeaker } from './types';

type Range = { startMs: number; endMs: number };
/** Import-label suggestions ride along on the speaker read (speakers.mjs). */
type Voice = MeetingSpeaker & { suggestions?: { participantId: string; source: string }[] };

const playable = (meeting: CompanyMeeting) =>
  !!meeting.file &&
  (meeting.file.mimeType.startsWith('audio/') || meeting.file.mimeType === 'video/mp4');

/** Plays a voice's sample rows one after another through the canvas player. */
function useSamplePlayer(player: MeetingPlayerRef) {
  const [playing, setPlaying] = useState<string | null>(null);
  const queue = useRef<Range[]>([]);
  const stop = useCallback(() => {
    queue.current = [];
    player.current?.pause();
    setPlaying(null);
  }, [player]);
  useEffect(() => {
    const media = player.current;
    if (!media || !playing) return;
    const onTime = () => {
      const current = queue.current[0];
      if (!current || media.currentTime * 1000 < current.endMs) return;
      queue.current = queue.current.slice(1);
      if (queue.current.length) media.currentTime = queue.current[0].startMs / 1000;
      else stop();
    };
    media.addEventListener('timeupdate', onTime);
    return () => media.removeEventListener('timeupdate', onTime);
  }, [player, playing, stop]);
  const play = (label: string, ranges: Range[]) => {
    const media = player.current;
    if (!media || !ranges.length) return;
    queue.current = ranges;
    media.currentTime = ranges[0].startMs / 1000;
    setPlaying(label);
    void media.play().catch(() => stop());
  };
  return { playing, play, stop };
}

type Pending = {
  voice: MeetingSpeaker;
  target: SpeakerTarget & { kind: 'participant' };
  owner: MeetingSpeaker;
};

/** The write a picked target means for a whole voice. */
async function applyVoiceTarget(
  meeting: CompanyMeeting,
  voice: MeetingSpeaker,
  target: SpeakerTarget,
) {
  if (target.kind === 'voice')
    return meetingsApi.mergeSpeaker(meeting.id, voice.label, target.label);
  if (target.kind === 'not_participant')
    return meetingsApi.updateSpeaker(meeting.id, voice.label, { kind: 'not_participant' });
  if (target.kind === 'guest')
    return meetingsApi.updateSpeaker(meeting.id, voice.label, { guest: { name: target.name } });
  const participantId = await participantFor(meeting.id, target);
  return meetingsApi.updateSpeaker(meeting.id, voice.label, { participantId });
}

function NotesState({
  meeting,
  changed,
  copy,
}: {
  meeting: CompanyMeeting;
  changed: boolean;
  copy: SpeakersCopy;
}) {
  if (!changed) return null;
  const status = meeting.notes?.status;
  if (status === 'stale' || status === 'generating')
    return (
      <p className="ws-speaker-status" role="status">
        {copy.notesRefreshing}
      </p>
    );
  if (status === 'current')
    return (
      <p className="ws-speaker-status done" role="status">
        {copy.notesUpToDate}
      </p>
    );
  return null;
}

function MergeChoice({
  meeting,
  pending,
  copy,
  onMerge,
  onName,
  onCancel,
}: {
  meeting: CompanyMeeting;
  pending: Pending;
  copy: SpeakersCopy;
  onMerge: () => void;
  onName: () => void;
  onCancel: () => void;
}) {
  const name = targetName(pending.target, copy);
  return (
    <div
      className="ws-speaker-scope"
      role="group"
      aria-label={voiceName(meeting, pending.owner, copy)}
    >
      <button className="ws-button" onClick={onMerge}>
        {formatCopy(copy.mergeWithVoice, { name })}
      </button>
      <button className="ws-button" onClick={onName}>
        {formatCopy(copy.nameAnyway, { name })}
      </button>
      <button className="ws-citation" onClick={onCancel}>
        {copy.cancel}
      </button>
    </div>
  );
}

function VoiceChip({
  meeting,
  voice,
  copy,
  open,
  busy,
  playing,
  canPlay,
  onToggle,
  onPlay,
  onUnmerge,
  editable,
  children,
}: {
  meeting: CompanyMeeting;
  voice: MeetingSpeaker;
  copy: SpeakersCopy;
  open: boolean;
  busy: boolean;
  playing: boolean;
  canPlay: boolean;
  editable: boolean;
  onToggle: () => void;
  onPlay: () => void;
  onUnmerge: (label: string) => void;
  children: ReactNode;
}) {
  const merged = mergedInto(meeting, voice.label);
  return (
    <li className={`ws-speaker-chip ${voice.kind}`} aria-busy={busy}>
      <div className="ws-speaker-chip-row">
        {editable ? (
          <button
            className="ws-speaker-name"
            aria-expanded={open}
            title={copy.pickPerson}
            onClick={onToggle}
          >
            {voiceName(meeting, voice, copy)}
            {voice.kind === 'not_participant' && <small>{copy.notParticipant}</small>}
          </button>
        ) : (
          <span className="ws-speaker-name">
            {voiceName(meeting, voice, copy)}
            {voice.kind === 'not_participant' && <small>{copy.notParticipant}</small>}
          </span>
        )}
        <span className="ws-speaker-meta">
          {formatCopy(copy.talkTime, {
            minutes: talkMinutes(voice.speechMs),
            rows: voice.segmentCount,
          })}
        </span>
        {canPlay && !!voice.sampleSegments.length && (
          <button className="ws-citation" onClick={onPlay}>
            {playing ? copy.stopSamples : copy.playSamples}
          </button>
        )}
      </div>
      {merged.map((child) => (
        <div key={child.label} className="ws-speaker-merged">
          <span>{formatCopy(copy.mergedHere, { name: voiceName(meeting, child, copy) })}</span>
          {editable && (
            <button className="ws-citation" disabled={busy} onClick={() => onUnmerge(child.label)}>
              {copy.unmerge}
            </button>
          )}
        </div>
      ))}
      {children}
    </li>
  );
}

export function MeetingSpeakersPanel({ meeting, player, onChanged }: MeetingSpeakersPanelProps) {
  const ws = useWorkspace();
  const copy = useSpeakersCopy();
  const [open, setOpen] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [changed, setChanged] = useState(false);
  const members = useCompanyMembers(open !== null);
  const samples = useSamplePlayer(player);
  const reload = useCallback(() => {
    setChanged(true);
    onChanged();
  }, [onChanged]);
  useSpeakerChanges(meeting.id, reload);
  const voices = panelVoices(meeting);
  if (!voices.length) return null;

  async function run(write: () => Promise<unknown>) {
    setBusy(true);
    try {
      await write();
      setOpen(null);
      setPending(null);
      reload();
    } catch (failure) {
      ws.notify(failureCopy(failure, copy));
    } finally {
      setBusy(false);
    }
  }
  function pick(voice: MeetingSpeaker, target: SpeakerTarget) {
    const owner =
      target.kind === 'participant'
        ? voiceOfPerson(meeting, target.participantId, voice.label)
        : null;
    if (owner && target.kind === 'participant') setPending({ voice, target, owner });
    else void run(() => applyVoiceTarget(meeting, voice, target));
  }
  const canPlay = playable(meeting);
  const editable = meeting.canEdit !== false;
  return (
    <section className="ws-speakers" aria-label={copy.speakersTitle}>
      <header>
        <h2>{copy.speakersTitle}</h2>
        <NotesState meeting={meeting} changed={changed} copy={copy} />
      </header>
      <p className="ws-page-note">{copy.speakersHint}</p>
      <VoiceStatusLine meeting={meeting} onChanged={reload} />
      {editable && <MeetingVoiceEnrolment meeting={meeting} onChanged={reload} />}
      <VoiceReviewEntry meeting={meeting} player={player} onChanged={reload} />
      <ul className="ws-speaker-chips">
        {voices.map((voice) => (
          <VoiceChip
            key={voice.label}
            meeting={meeting}
            voice={voice}
            copy={copy}
            open={open === voice.label}
            busy={busy}
            playing={samples.playing === voice.label}
            canPlay={canPlay}
            editable={editable}
            onToggle={() => {
              setPending(null);
              setOpen(open === voice.label ? null : voice.label);
            }}
            onPlay={() =>
              samples.playing === voice.label
                ? samples.stop()
                : samples.play(voice.label, sampleRanges(meeting, voice))
            }
            onUnmerge={(label) => void run(() => meetingsApi.unmergeSpeaker(meeting.id, label))}
          >
            <SpeakerVoiceSuggestions meeting={meeting} voice={voice} onChanged={reload} />
            {editable && open === voice.label && pending?.voice.label === voice.label && (
              <MergeChoice
                meeting={meeting}
                pending={pending}
                copy={copy}
                onMerge={() =>
                  void run(() =>
                    meetingsApi.mergeSpeaker(meeting.id, voice.label, pending.owner.label),
                  )
                }
                onName={() => void run(() => applyVoiceTarget(meeting, voice, pending.target))}
                onCancel={() => setPending(null)}
              />
            )}
            {editable && open === voice.label && !pending && (
              <div className="ws-speaker-menu" role="dialog" aria-label={copy.pickPerson}>
                <SpeakerTargetPicker
                  meeting={meeting}
                  members={members}
                  copy={copy}
                  options={{
                    voices: { except: voice.label, title: copy.mergeInto },
                    notParticipant: voice.kind !== 'not_participant',
                    suggested: ((voice as Voice).suggestions ?? []).map(
                      (item) => item.participantId,
                    ),
                  }}
                  onPick={(target) => pick(voice, target)}
                />
              </div>
            )}
          </VoiceChip>
        ))}
      </ul>
    </section>
  );
}
